import AsyncStorage from '@react-native-async-storage/async-storage';
import NetInfo, { NetInfoState } from '@react-native-community/netinfo';
import { apiService } from './apiService';

const CACHE_KEYS = {
  POSTS: '@padrinho_community_posts_cache',
  OFFLINE_QUEUE: '@padrinho_community_offline_queue',
  CACHE_TIMESTAMP: '@padrinho_community_cache_ts',
};

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export interface CommunityAuthor {
  id: string;
  full_name: string;
  avatar_url: string | null;
  is_admin?: boolean;
  created_at?: string;
  username?: string | null;
}

export interface CommunityPost {
  id: string;
  user_id: string;
  content: string;
  topics: string[];
  is_anonymous: boolean;
  support_count: number;
  encourage_count: number;
  status: string;
  created_at: string;
  updated_at: string;
  author: CommunityAuthor;
  user_has_supported: boolean;
  encouragements?: CommunityEncouragement[] | undefined;
}

export interface CommunityEncouragement {
  id: string;
  post_id: string;
  user_id: string;
  content: string;
  status: string;
  created_at: string;
  author: CommunityAuthor;
}

export interface OfflineAction {
  id: string;
  type: 'create_post' | 'support' | 'unsupport' | 'encourage';
  payload: any;
  timestamp: number;
}

export interface GetPostsOptions {
  topic?: string;
  page?: number;
  limit?: number;
  sort?: 'relevance' | 'recent';
}

export interface CreatePostData {
  content: string;
  topics?: string[];
  is_anonymous?: boolean;
}

class CommunityServiceClass {
  private isOnline: boolean = true;
  private networkListenerInitialized: boolean = false;

  constructor() {
    // Defer network listener initialization to avoid constructor errors
    setTimeout(() => this.initNetworkListener(), 0);
  }

  private initNetworkListener() {
    if (this.networkListenerInitialized) return;
    
    try {
      NetInfo.addEventListener((state: NetInfoState) => {
        const wasOffline = !this.isOnline;
        this.isOnline = state.isConnected ?? true;
        
        if (wasOffline && this.isOnline) {
          console.log('📶 [Community] Back online, syncing offline queue...');
          this.syncOfflineQueue();
        }
      });
      this.networkListenerInitialized = true;
      console.log('📶 [Community] Network listener initialized');
    } catch (error) {
      console.warn('⚠️ [Community] Failed to initialize network listener:', error);
    }
  }

  async checkOnlineStatus(): Promise<boolean> {
    try {
      const state = await NetInfo.fetch();
      this.isOnline = state.isConnected ?? true;
    } catch (error) {
      console.warn('⚠️ [Community] Failed to check network status, assuming online');
      this.isOnline = true;
    }
    return this.isOnline;
  }

  // ============================================
  // CACHE LAYER
  // ============================================

  private async getCachedPosts(): Promise<CommunityPost[]> {
    try {
      const cached = await AsyncStorage.getItem(CACHE_KEYS.POSTS);
      if (cached) {
        return JSON.parse(cached);
      }
    } catch (error) {
      console.error('❌ [Community] Error reading cache:', error);
    }
    return [];
  }

  private async setCachedPosts(posts: CommunityPost[]): Promise<void> {
    try {
      await AsyncStorage.setItem(CACHE_KEYS.POSTS, JSON.stringify(posts));
      await AsyncStorage.setItem(CACHE_KEYS.CACHE_TIMESTAMP, Date.now().toString());
    } catch (error) {
      console.error('❌ [Community] Error writing cache:', error);
    }
  }

  private async isCacheValid(): Promise<boolean> {
    try {
      const timestamp = await AsyncStorage.getItem(CACHE_KEYS.CACHE_TIMESTAMP);
      if (!timestamp) return false;
      return Date.now() - parseInt(timestamp) < CACHE_TTL;
    } catch {
      return false;
    }
  }

  async clearCache(): Promise<void> {
    await AsyncStorage.multiRemove([
      CACHE_KEYS.POSTS,
      CACHE_KEYS.CACHE_TIMESTAMP,
    ]);
  }

  // ============================================
  // OFFLINE QUEUE
  // ============================================

  private async getOfflineQueue(): Promise<OfflineAction[]> {
    try {
      const queue = await AsyncStorage.getItem(CACHE_KEYS.OFFLINE_QUEUE);
      if (queue) {
        return JSON.parse(queue);
      }
    } catch (error) {
      console.error('❌ [Community] Error reading offline queue:', error);
    }
    return [];
  }

  private async addToOfflineQueue(action: OfflineAction): Promise<void> {
    const queue = await this.getOfflineQueue();
    queue.push(action);
    await AsyncStorage.setItem(CACHE_KEYS.OFFLINE_QUEUE, JSON.stringify(queue));
    console.log('📥 [Community] Added action to offline queue:', action.type);
  }

  private async clearOfflineQueue(): Promise<void> {
    await AsyncStorage.removeItem(CACHE_KEYS.OFFLINE_QUEUE);
  }

  async syncOfflineQueue(): Promise<{ synced: number; failed: number }> {
    const queue = await this.getOfflineQueue();
    if (queue.length === 0) {
      return { synced: 0, failed: 0 };
    }

    console.log('🔄 [Community] Syncing', queue.length, 'offline actions...');

    let synced = 0;
    let failed = 0;
    const failedActions: OfflineAction[] = [];

    for (const action of queue) {
      try {
        switch (action.type) {
          case 'create_post':
            await this.createPostOnline(action.payload);
            break;
          case 'support':
            await this.supportPostOnline(action.payload.postId);
            break;
          case 'unsupport':
            await this.unsupportPostOnline(action.payload.postId);
            break;
          case 'encourage':
            await this.addEncouragementOnline(action.payload.postId, action.payload.content);
            break;
        }
        synced++;
      } catch (error) {
        console.error('❌ [Community] Failed to sync action:', action.type, error);
        failed++;
        // Keep failed actions for retry
        failedActions.push(action);
      }
    }

    // Only keep failed actions in the queue for later retry
    if (failedActions.length > 0) {
      await AsyncStorage.setItem(CACHE_KEYS.OFFLINE_QUEUE, JSON.stringify(failedActions));
      console.log('⚠️ [Community] Retained', failedActions.length, 'failed actions for retry');
    } else {
      await this.clearOfflineQueue();
    }

    console.log('✅ [Community] Sync complete:', { synced, failed });
    return { synced, failed };
  }

  // ============================================
  // POSTS - READ OPERATIONS
  // ============================================

  async getPosts(options: GetPostsOptions = {}): Promise<{
    posts: CommunityPost[];
    fromCache: boolean;
    pagination: { page: number; limit: number; hasMore: boolean };
  }> {
    const { topic, page = 1, limit = 20, sort = 'relevance' } = options;

    // Cache is only valid for relevance sort (default), page 1, no topic filter
    // This ensures cache ordering matches what user expects
    const canUseCache = page === 1 && sort === 'relevance' && (!topic || topic === 'all');
    
    if (canUseCache && await this.isCacheValid()) {
      const cached = await this.getCachedPosts();
      if (cached.length > 0) {
        console.log('📦 [Community] Returning cached posts (relevance, page 1)');
        return {
          posts: cached.slice(0, limit),
          fromCache: true,
          pagination: { page: 1, limit, hasMore: cached.length > limit }
        };
      }
    }

    // Try to fetch from server
    if (await this.checkOnlineStatus()) {
      try {
        const queryParams = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
          sort,
          ...(topic && topic !== 'all' ? { topic } : {})
        });

        const response = await apiService.get<{
          posts: CommunityPost[];
          pagination: { page: number; limit: number; hasMore: boolean };
        }>(`/api/community/posts?${queryParams}`);

        if (response.success && response.data) {
          // Only cache relevance-sorted, first page, no topic filter
          // This ensures cached data has consistent ordering
          if (page === 1 && sort === 'relevance' && (!topic || topic === 'all')) {
            await this.setCachedPosts(response.data.posts);
          }
          return {
            posts: response.data.posts,
            fromCache: false,
            pagination: response.data.pagination
          };
        }
      } catch (error) {
        console.error('❌ [Community] Error fetching posts from server:', error);
      }
    }

    // Fallback to cache
    console.log('📦 [Community] Offline, returning cached posts');
    const cached = await this.getCachedPosts();
    let filteredPosts = cached;
    if (topic && topic !== 'all') {
      filteredPosts = cached.filter(p => p.topics.includes(topic));
    }
    return {
      posts: filteredPosts,
      fromCache: true,
      pagination: { page: 1, limit, hasMore: false }
    };
  }

  async getPostById(postId: string): Promise<CommunityPost | null> {
    if (await this.checkOnlineStatus()) {
      try {
        const response = await apiService.get<{ post: CommunityPost }>(
          `/api/community/posts/${postId}`
        );
        if (response.success && response.data) {
          return response.data.post;
        }
      } catch (error) {
        console.error('❌ [Community] Error fetching post:', error);
      }
    }

    // Try cache
    const cached = await this.getCachedPosts();
    return cached.find(p => p.id === postId) || null;
  }

  async getFollowingPosts(options: { page?: number; limit?: number } = {}): Promise<{
    posts: CommunityPost[];
    pagination: { page: number; limit: number; hasMore: boolean };
  }> {
    const { page = 1, limit = 20 } = options;

    if (await this.checkOnlineStatus()) {
      try {
        const queryParams = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
        });

        const response = await apiService.get<{
          posts: CommunityPost[];
          pagination: { page: number; limit: number; hasMore: boolean };
        }>(`/api/community/following/posts?${queryParams}`);

        if (response.success && response.data) {
          console.log(`📥 [Community] Fetched ${response.data.posts.length} following posts`);
          return {
            posts: response.data.posts,
            pagination: response.data.pagination
          };
        }
      } catch (error) {
        console.error('❌ [Community] Error fetching following posts:', error);
      }
    }

    // Return empty array if offline or error
    return {
      posts: [],
      pagination: { page: 1, limit, hasMore: false }
    };
  }

  // ============================================
  // POSTS - WRITE OPERATIONS
  // ============================================

  private async createPostOnline(data: CreatePostData): Promise<CommunityPost> {
    const response = await apiService.post<{ post: CommunityPost }>(
      '/api/community/posts',
      data
    );
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to create post');
    }
    // RJ-3b — invalidate today's recovery plan so the Missões row for
    // `publicar_comunidade` flips to completed on next visit. Dynamic
    // import avoids a service-graph cycle.
    import('./recoveryPlanService')
      .then(({ recoveryPlanService }) => recoveryPlanService.invalidateToday())
      .catch(() => {});
    return response.data.post;
  }

  async createPost(data: CreatePostData): Promise<{
    post: CommunityPost;
    queued: boolean;
  }> {
    if (await this.checkOnlineStatus()) {
      try {
        const post = await this.createPostOnline(data);
        
        // Update cache optimistically
        const cached = await this.getCachedPosts();
        await this.setCachedPosts([post, ...cached]);
        
        return { post, queued: false };
      } catch (error) {
        console.error('❌ [Community] Error creating post online:', error);
        throw error;
      }
    }

    // Offline: create optimistic post and queue
    const optimisticPost: CommunityPost = {
      id: `temp_${Date.now()}`,
      user_id: 'pending',
      content: data.content,
      topics: data.topics || [],
      is_anonymous: data.is_anonymous || false,
      support_count: 0,
      encourage_count: 0,
      status: 'pending',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      author: { id: 'pending', full_name: 'Você', avatar_url: null },
      user_has_supported: false,
    };

    await this.addToOfflineQueue({
      id: optimisticPost.id,
      type: 'create_post',
      payload: data,
      timestamp: Date.now(),
    });

    // Add to cache
    const cached = await this.getCachedPosts();
    await this.setCachedPosts([optimisticPost, ...cached]);

    return { post: optimisticPost, queued: true };
  }

  async deletePost(postId: string): Promise<boolean> {
    if (!await this.checkOnlineStatus()) {
      throw new Error('Cannot delete posts while offline');
    }

    const response = await apiService.delete(`/api/community/posts/${postId}`);
    if (response.success) {
      // Remove from cache
      const cached = await this.getCachedPosts();
      await this.setCachedPosts(cached.filter(p => p.id !== postId));
      return true;
    }
    return false;
  }

  async updatePost(postId: string, data: { content: string; topics?: string[] }): Promise<CommunityPost | null> {
    if (!await this.checkOnlineStatus()) {
      throw new Error('Cannot update posts while offline');
    }

    const response = await apiService.put<{ post: CommunityPost }>(
      `/api/community/posts/${postId}`,
      data
    );
    
    if (response.success && response.data) {
      // Update cache
      const cached = await this.getCachedPosts();
      const updatedCache = cached.map(p => 
        p.id === postId ? response.data!.post : p
      );
      await this.setCachedPosts(updatedCache);
      return response.data.post;
    }
    return null;
  }

  // ============================================
  // SUPPORT OPERATIONS
  // ============================================

  private async supportPostOnline(postId: string): Promise<number> {
    const response = await apiService.post<{ support_count: number }>(
      `/api/community/posts/${postId}/support`
    );
    if (!response.success) {
      throw new Error(response.error || 'Failed to support post');
    }
    // RJ-3b — supporting a post counts toward `apoiar_membro_comunidade`.
    import('./recoveryPlanService')
      .then(({ recoveryPlanService }) => recoveryPlanService.invalidateToday())
      .catch(() => {});
    return response.data?.support_count || 0;
  }

  private async unsupportPostOnline(postId: string): Promise<number> {
    const response = await apiService.delete<{ support_count: number }>(
      `/api/community/posts/${postId}/support`
    );
    if (!response.success) {
      throw new Error(response.error || 'Failed to remove support');
    }
    return response.data?.support_count || 0;
  }

  async supportPost(postId: string): Promise<{ support_count: number; queued: boolean }> {
    // Optimistic update in cache
    const cached = await this.getCachedPosts();
    const postIndex = cached.findIndex(p => p.id === postId);
    const existingPost = postIndex !== -1 ? cached[postIndex] : null;
    
    if (existingPost) {
      cached[postIndex] = {
        id: existingPost.id,
        user_id: existingPost.user_id,
        content: existingPost.content,
        topics: existingPost.topics,
        is_anonymous: existingPost.is_anonymous,
        support_count: existingPost.support_count + 1,
        encourage_count: existingPost.encourage_count,
        status: existingPost.status,
        created_at: existingPost.created_at,
        updated_at: existingPost.updated_at,
        author: existingPost.author,
        user_has_supported: true,
        encouragements: existingPost.encouragements,
      };
      await this.setCachedPosts(cached);
    }

    if (await this.checkOnlineStatus()) {
      try {
        const count = await this.supportPostOnline(postId);
        return { support_count: count, queued: false };
      } catch (error) {
        // Revert optimistic update
        const postToRevert = cached[postIndex];
        if (existingPost && postIndex !== -1 && postToRevert) {
          postToRevert.support_count -= 1;
          postToRevert.user_has_supported = false;
          await this.setCachedPosts(cached);
        }
        throw error;
      }
    }

    // Queue for later
    await this.addToOfflineQueue({
      id: `support_${postId}_${Date.now()}`,
      type: 'support',
      payload: { postId },
      timestamp: Date.now(),
    });

    return {
      support_count: existingPost?.support_count ? existingPost.support_count + 1 : 0,
      queued: true,
    };
  }

  async unsupportPost(postId: string): Promise<{ support_count: number; queued: boolean }> {
    // Optimistic update in cache
    const cached = await this.getCachedPosts();
    const postIndex = cached.findIndex(p => p.id === postId);
    const existingPost = postIndex !== -1 ? cached[postIndex] : null;
    
    if (existingPost) {
      cached[postIndex] = {
        id: existingPost.id,
        user_id: existingPost.user_id,
        content: existingPost.content,
        topics: existingPost.topics,
        is_anonymous: existingPost.is_anonymous,
        support_count: Math.max(0, existingPost.support_count - 1),
        encourage_count: existingPost.encourage_count,
        status: existingPost.status,
        created_at: existingPost.created_at,
        updated_at: existingPost.updated_at,
        author: existingPost.author,
        user_has_supported: false,
        encouragements: existingPost.encouragements,
      };
      await this.setCachedPosts(cached);
    }

    if (await this.checkOnlineStatus()) {
      try {
        const count = await this.unsupportPostOnline(postId);
        return { support_count: count, queued: false };
      } catch (error) {
        // Revert optimistic update
        const postToRevert = cached[postIndex];
        if (existingPost && postIndex !== -1 && postToRevert) {
          postToRevert.support_count += 1;
          postToRevert.user_has_supported = true;
          await this.setCachedPosts(cached);
        }
        throw error;
      }
    }

    // Queue for later
    await this.addToOfflineQueue({
      id: `unsupport_${postId}_${Date.now()}`,
      type: 'unsupport',
      payload: { postId },
      timestamp: Date.now(),
    });

    return {
      support_count: existingPost?.support_count ? Math.max(0, existingPost.support_count - 1) : 0,
      queued: true,
    };
  }

  async toggleSupport(postId: string, currentlySupported: boolean): Promise<{
    support_count: number;
    user_has_supported: boolean;
    queued: boolean;
  }> {
    if (currentlySupported) {
      const result = await this.unsupportPost(postId);
      return { ...result, user_has_supported: false };
    } else {
      const result = await this.supportPost(postId);
      return { ...result, user_has_supported: true };
    }
  }

  // ============================================
  // ENCOURAGEMENT OPERATIONS
  // ============================================

  private async addEncouragementOnline(
    postId: string,
    content: string
  ): Promise<{ encouragement: CommunityEncouragement; encourage_count: number }> {
    const response = await apiService.post<{
      encouragement: CommunityEncouragement;
      encourage_count: number;
    }>(`/api/community/posts/${postId}/encourage`, { content });
    
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to add encouragement');
    }
    // RJ-3b — encouraging another member also counts as community support.
    import('./recoveryPlanService')
      .then(({ recoveryPlanService }) => recoveryPlanService.invalidateToday())
      .catch(() => {});
    return response.data;
  }

  async addEncouragement(postId: string, content: string): Promise<{
    encouragement: CommunityEncouragement | null;
    encourage_count: number;
    queued: boolean;
  }> {
    // Optimistic update in cache
    const cached = await this.getCachedPosts();
    const postIndex = cached.findIndex(p => p.id === postId);
    const existingPost = postIndex !== -1 ? cached[postIndex] : null;
    
    if (existingPost) {
      cached[postIndex] = {
        id: existingPost.id,
        user_id: existingPost.user_id,
        content: existingPost.content,
        topics: existingPost.topics,
        is_anonymous: existingPost.is_anonymous,
        support_count: existingPost.support_count,
        encourage_count: existingPost.encourage_count + 1,
        status: existingPost.status,
        created_at: existingPost.created_at,
        updated_at: existingPost.updated_at,
        author: existingPost.author,
        user_has_supported: existingPost.user_has_supported,
        encouragements: existingPost.encouragements,
      };
      await this.setCachedPosts(cached);
    }

    if (await this.checkOnlineStatus()) {
      try {
        const result = await this.addEncouragementOnline(postId, content);
        return { encouragement: result.encouragement, encourage_count: result.encourage_count, queued: false };
      } catch (error) {
        // Revert optimistic update
        const postToRevert = cached[postIndex];
        if (existingPost && postIndex !== -1 && postToRevert) {
          postToRevert.encourage_count -= 1;
          await this.setCachedPosts(cached);
        }
        throw error;
      }
    }

    // Queue for later
    await this.addToOfflineQueue({
      id: `encourage_${postId}_${Date.now()}`,
      type: 'encourage',
      payload: { postId, content },
      timestamp: Date.now(),
    });

    return {
      encouragement: null,
      encourage_count: existingPost?.encourage_count ? existingPost.encourage_count + 1 : 0,
      queued: true,
    };
  }

  // ============================================
  // REPORT OPERATIONS
  // ============================================

  async reportPost(postId: string, reason: string, description?: string): Promise<boolean> {
    if (!await this.checkOnlineStatus()) {
      throw new Error('Cannot report posts while offline');
    }

    const response = await apiService.post(`/api/community/posts/${postId}/report`, {
      reason,
      description,
    });

    return response.success;
  }

  // ============================================
  // USER STATS
  // ============================================

  async getUserStats(userId: string): Promise<{
    following: number;
    friends: number;
    posts: number;
  }> {
    try {
      const response = await apiService.get<{
        stats: { following: number; friends: number; posts: number };
      }>(`/api/community/user/${userId}/stats`);

      if (response.success && response.data) {
        return response.data.stats;
      }
    } catch (error) {
      console.error('❌ [Community] Error fetching user stats:', error);
    }

    // Fallback to zeros
    return { following: 0, friends: 0, posts: 0 };
  }

  // ============================================
  // USER POSTS
  // ============================================

  async getUserPosts(userId: string, page = 1, limit = 20): Promise<{
    posts: CommunityPost[];
    pagination: { page: number; limit: number; hasMore: boolean };
  }> {
    if (await this.checkOnlineStatus()) {
      try {
        const response = await apiService.get<{
          posts: CommunityPost[];
          pagination: { page: number; limit: number; hasMore: boolean };
        }>(`/api/community/user/${userId}/posts?page=${page}&limit=${limit}`);

        if (response.success && response.data) {
          return response.data;
        }
      } catch (error) {
        console.error('❌ [Community] Error fetching user posts:', error);
      }
    }

    // Fallback: filter cached posts
    const cached = await this.getCachedPosts();
    const userPosts = cached.filter(p => p.user_id === userId && !p.is_anonymous);
    return {
      posts: userPosts,
      pagination: { page: 1, limit, hasMore: false },
    };
  }

  // ============================================
  // FOLLOW / COMPANIONS
  // ============================================

  async checkFollowingStatus(userId: string): Promise<{ isFollowing: boolean; isSelf: boolean }> {
    try {
      const response = await apiService.get<{ isFollowing: boolean; isSelf: boolean }>(
        `/api/community/users/${userId}/following-status`
      );
      
      if (response.success && response.data) {
        return response.data;
      }
      return { isFollowing: false, isSelf: false };
    } catch (error) {
      console.error('❌ [Community] Error checking follow status:', error);
      return { isFollowing: false, isSelf: false };
    }
  }

  async followUser(userId: string): Promise<boolean> {
    try {
      const response = await apiService.post<{ success: boolean }>(
        `/api/community/users/${userId}/follow`
      );
      return response.success;
    } catch (error) {
      console.error('❌ [Community] Error following user:', error);
      return false;
    }
  }

  async unfollowUser(userId: string): Promise<boolean> {
    try {
      const response = await apiService.delete<{ success: boolean }>(
        `/api/community/users/${userId}/follow`
      );
      return response.success;
    } catch (error) {
      console.error('❌ [Community] Error unfollowing user:', error);
      return false;
    }
  }

  // ============================================
  // UTILITY
  // ============================================

  async getOfflineQueueStatus(): Promise<{ pendingActions: number }> {
    const queue = await this.getOfflineQueue();
    return { pendingActions: queue.length };
  }
}

// Simple singleton pattern with error handling
let _instance: CommunityServiceClass;

try {
  _instance = new CommunityServiceClass();
} catch (error) {
  console.error('❌ [Community] Failed to create service instance:', error);
  // Create minimal fallback
  _instance = {
    getPosts: async () => ({ posts: [], fromCache: true, pagination: { page: 1, limit: 20, hasMore: false } }),
    getPostById: async () => null,
    createPost: async () => null,
    deletePost: async () => ({ success: false }),
    supportPost: async () => ({ success: false, newCount: 0 }),
    unsupportPost: async () => ({ success: false, newCount: 0 }),
    addEncouragement: async () => null,
    reportPost: async () => ({ success: false }),
    getUserPosts: async () => ({ posts: [], pagination: { page: 1, limit: 20, hasMore: false } }),
    clearCache: async () => {},
    syncOfflineQueue: async () => ({ synced: 0, failed: 0 }),
    getOfflineQueueStatus: async () => ({ pendingActions: 0 }),
    checkOnlineStatus: async () => true,
  } as unknown as CommunityServiceClass;
}

export const communityService = _instance;
